import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider, Modal } from "antd";
import ru from "antd/es/locale/ru_RU";
import { tz } from "moment-timezone";
import "moment/locale/ru";
import "./services/AuthServiceInt";
import LocalStorageService from "./services/LocalStorageService";
import { fetchTenantById, fetchTenantProperties } from "./store/slices/tenants";
import Router from "./router/Router";
import { generateRoutes } from "./router/router.config";
import { fetchObjectTypes } from "./store/slices/rtkDictionaries";
import { fetchConfigData } from "./store/slices/config";
import { checkIsLoaded } from "./lib/utils/checkLoadingStatus";
import useBlockingMode from "./lib/hooks/useBlockingMode";
import "./styles/styles.less"

function App() {
  const dispatch = useDispatch();
  const { configLoading } = useSelector((state) => state.config);
  const { tenant } = useSelector((state) => state.tenants);
  const tenantId = LocalStorageService.getTenantId();
  const isAuthenticated = !!LocalStorageService.getAccessToken();

  const [isTenantChanged, setIsTenantChanged] = useState(false);

  const timer = useRef();

  const reload = () => {
    window.location.assign("/");
  };

  // Защита от открытия в двух вкладках разных тенантов
  const checkTenant = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      const tenantId = LocalStorageService.getTenantId();
      const isAuthenticated = !!LocalStorageService.getAccessToken();
      if (isAuthenticated && tenant?.id) {
        if (tenantId !== String(tenant.id) && !isTenantChanged) {
          setIsTenantChanged(true);
          Modal.warning({
            content: "Тенант был изменен, страница будет перезагружена!",
            onCancel: reload,
            onOk: reload,
          });
        }
      }
    }, 100);
  }, [isTenantChanged, tenant, tenantId, isAuthenticated, reload]);

  useEffect(() => {
    window.addEventListener("mousemove", checkTenant);
  }, [tenant]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchTenantProperties(tenantId));
      if (!checkIsLoaded(configLoading)) dispatch(fetchConfigData());
      dispatch(fetchObjectTypes());
    }
    tenantId && dispatch(fetchTenantById({ tenantId }));
  }, [dispatch, tenantId, isAuthenticated]);

  useEffect(() => {
    if (tenant?.defaultTimezoneName) {
      tz.setDefault(tenant.defaultTimezoneName);
    }
  }, [tenant]);

  const isBlockingMode = useBlockingMode();

  const routes = generateRoutes({ isBlockingMode });

  return (
    <ConfigProvider locale={ru}>
      <BrowserRouter>
        <Router routes={routes} />
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
